 
import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import {
  CompanyTableRow,
  CompaniesRequest,
  CompaniesFuzzySearchProps,
  TotalDashboardWidgetsResponse,
  CompaniesDetailsResponse,
  CompaniesWildcardSearchProps,
} from '@uptime-bff/api-contract';
import { CountryCode } from '@uptime-bff/api-contract/dist/common';
import { SearchType } from 'feature/assets/store';

type CompanyFiltersType = CompaniesRequest['filters'];
type CompanySortingType = CompaniesRequest['sorting'];
type CompanyListFilterSearchType = {
  query: string;
  field: CompaniesFuzzySearchProps[] | CompaniesWildcardSearchProps;
  searchType?: SearchType;
};

/**
 * A favourite-company selection that has been made in the UI but not yet persisted.
 * Keyed in state by `companyId` (the diamGroupId, i.e. `company.iamId`), which is the
 * identity the dashboard favourites API uses. `rowId` is the `companiesList` key
 * (`company.id`) so the optimistic flag can be reverted on a save failure.
 */
export interface PendingFavourite {
  companyId: string;
  companyName: string;
  rowId: string;
}

export const defaultState: CompanyListState = {
  totalCompaniesCount: undefined,
  activeCompanyFilters: undefined,
  companiesList: {},
  companiesSorting: undefined,
  companyMapActiveCompanyId: undefined,
  companySearch: {
    query: '',
    field: [CompaniesFuzzySearchProps.Name],
    searchType: SearchType.Fuzzy,
  },
  companyDashboardWidgets: {
    totalNumberOfDashboardWidgets: 0,
  },
  pendingFavourites: {},
  selectedCompany: undefined,
};

interface CompanyListState {
  totalCompaniesCount?: number;
  activeCompanyFilters: CompanyFiltersType;
  companiesList: { [key: string]: CompanyTableRow };
  companiesSorting?: CompanySortingType;
  companyMapActiveCompanyId?: string;
  companySearch?: CompanyListFilterSearchType;
  companyDashboardWidgets?: TotalDashboardWidgetsResponse;
  pendingFavourites: { [companyId: string]: PendingFavourite };
  selectedCompany?: CompanyTableRow;
}

const companyListToMap = (list: CompanyTableRow[]): { [key: string]: CompanyTableRow } => {
  const payloadCompaniesMap: { [key: string]: CompanyTableRow } = {};
  for (const company of list) {
    payloadCompaniesMap[company.id] = { ...company };
  }
  return payloadCompaniesMap;
};

// Re-apply optimistic favourite flags after the list is replaced/extended, so a list
// refetch does not visually revert selections the user made but that aren't saved yet.
const reapplyPendingFavouriteFlags = (state: CompanyListState) => {
  if (!state.pendingFavourites) {
    return;
  }
  for (const { rowId } of Object.values(state.pendingFavourites)) {
    if (state.companiesList[rowId]) {
      state.companiesList[rowId].hasDashboardWidget = true;
    }
  }
};

const slice = createSlice({
  name: 'companies',
  initialState: defaultState,
  reducers: {
    setSelectedCompany: (state, action: PayloadAction<CompanyTableRow | undefined>) => {
      state.selectedCompany = action.payload;
    },
    setTotalCompaniesCount: (state, action: PayloadAction<number | undefined>) => {
      state.totalCompaniesCount = action.payload;
    },
    setWidgetFromCompanyListToDashboard: (state, action: PayloadAction<CompanyTableRow>) => {
      const company = action.payload;
      if (state.companiesList[company.id]) {
        state.companiesList[company.id] = company;
      }
    },

    setWidgetFromCompanyDetailsToDashboard: (state, action: PayloadAction<CompaniesDetailsResponse>) => {
      const company = action.payload;
      if (state.companiesList[company.id]) {
        state.companiesList[company.id] = { ...state.companiesList[company.id] };
      }
    },

    setTotalNumberOfDashboardWidgets: (state, action: PayloadAction<TotalDashboardWidgetsResponse>) => {
      if (state.companyDashboardWidgets) {
        state.companyDashboardWidgets.totalNumberOfDashboardWidgets = action.payload.totalNumberOfDashboardWidgets;
      }
    },

    // Optimistically register a favourite selection (no network call). The single
    // batched write happens later in useFlushFavourites.
    addPendingFavourite: (state, action: PayloadAction<PendingFavourite>) => {
      const favourite = action.payload;
      if (!state.pendingFavourites) {
        state.pendingFavourites = {};
      }
      state.pendingFavourites[favourite.companyId] = favourite;
      if (state.companiesList[favourite.rowId]) {
        state.companiesList[favourite.rowId].hasDashboardWidget = true;
      }
    },

    // Drop pending entries once they have been persisted successfully.
    removePendingFavourites: (state, action: PayloadAction<string[]>) => {
      if (!state.pendingFavourites) {
        return;
      }
      for (const companyId of action.payload) {
        delete state.pendingFavourites[companyId];
      }
    },

    // Roll back optimistic selections when the batched save fails.
    revertPendingFavourites: (state, action: PayloadAction<PendingFavourite[]>) => {
      for (const favourite of action.payload) {
        delete state.pendingFavourites?.[favourite.companyId];
        if (state.companiesList[favourite.rowId]) {
          state.companiesList[favourite.rowId].hasDashboardWidget = false;
        }
      }
    },

    setSortingOnActiveSearch: (state, action: PayloadAction<CompanySortingType>) => {
      state.companiesSorting = action.payload;
    },
    setFilterByIdOnActiveSearch: (state, action: PayloadAction<string>) => {
      state.activeCompanyFilters = { ...state.activeCompanyFilters, id: action.payload };
    },

    setSearchOrFilterQuery: (state, action: PayloadAction<string>) => {
      state.companySearch = {
        field: state.companySearch?.field ?? [CompaniesFuzzySearchProps.Name],
        query: action.payload,
        searchType: state.companySearch?.searchType ?? SearchType.Fuzzy,
      };
      slice.caseReducers.setSearchOnActiveSearch(state);
    },

    // Wil need to be removed after wildcard search is released
    setSearchField: (state, action: PayloadAction<{ field: CompaniesFuzzySearchProps; selected: boolean }>) => {
      const currentProps: CompaniesFuzzySearchProps[] = Array.isArray(state.companySearch?.field)
        ? state.companySearch?.field
        : [];
      state.companySearch = {
        field: action.payload.selected
          ? [...currentProps, action.payload.field]
          : currentProps.filter((prop) => prop !== action.payload.field),
        query: state.companySearch?.query ?? '',
      };
    },

    setFuzzySearchField: (state, action: PayloadAction<{ field: CompaniesFuzzySearchProps; selected: boolean, value: string }>) => {
      if (action.payload.selected) {
        state.companySearch = {
          field: [action.payload.field] as CompaniesFuzzySearchProps[],
          query: state.companySearch?.query ?? '',
          searchType: SearchType.Fuzzy,
        };
      }

      if (state.companySearch?.query && state.companySearch?.query.length > 1) {
        slice.caseReducers.setSearchOnActiveSearch(state);
      }
    },

    setWildcardSearchField: (state, action: PayloadAction<{ field: CompaniesWildcardSearchProps; selected: boolean, value: string }>) => {
      if (action.payload.selected) {
        state.companySearch = {
          field: action.payload.field as CompaniesWildcardSearchProps,
          query: state.companySearch?.query ?? '',
          searchType: SearchType.Wildcard,
        };
      }
      if (state.companySearch?.query && state.companySearch.query.length > 1) {
        slice.caseReducers.setSearchOnActiveSearch(state);
      }
    },

    setSearchOnActiveSearch: (state) => {
      const checkLength = state.companySearch?.query.length
        && state.companySearch?.query.length > 1
        && state.companySearch?.field.length
        && state.companySearch?.field.length > 0;

      if (
        checkLength && state.companySearch?.searchType === SearchType.Fuzzy
      ) {
        state.activeCompanyFilters = {
          ...state.activeCompanyFilters,
          search: {
            query: state.companySearch.query,
            fields: state.companySearch.field as CompaniesFuzzySearchProps[],
          },
          wildcardSearch: undefined,
        };
      } else if (checkLength && state.companySearch?.searchType === SearchType.Wildcard) {
        state.activeCompanyFilters = {
          ...state.activeCompanyFilters,
          search: undefined,
          wildcardSearch: {
            query: state.companySearch.query,
            field: state.companySearch.field as CompaniesWildcardSearchProps,
          },
        };
      } else {
        state.activeCompanyFilters = {
          ...state.activeCompanyFilters,
          search: undefined,
          wildcardSearch: undefined,
        };
      }
    },

    clearSearchOnActiveSearch: (state) => {
      state.activeCompanyFilters = {
        ...state.activeCompanyFilters,
        search: undefined,
        wildcardSearch: undefined,
      };
      state.companySearch = {
        field: [CompaniesFuzzySearchProps.Name],
        query: '',
        searchType: SearchType.Fuzzy,
      };
    },

    setFiltersByCountryCodeOnActiveSearch: (state, action: PayloadAction<CountryCode[]>) => {
      state.activeCompanyFilters = {
        ...state.activeCompanyFilters,
        countryCode: action.payload.length > 0 ? action.payload : undefined,
      };
    },
    appendCompaniesToList: (state, action: PayloadAction<CompanyTableRow[]>) => {
      state.companiesList = { ...state.companiesList, ...companyListToMap(action.payload) };
      reapplyPendingFavouriteFlags(state);
    },
    setCompanyMapActiveCompnayId: (state, action: PayloadAction<string | undefined>) => {
      state.companyMapActiveCompanyId = action.payload;
    },
    setCompanies: (state, action: PayloadAction<CompanyTableRow[]>) => {
      state.companiesList = companyListToMap(action.payload);
      reapplyPendingFavouriteFlags(state);
    },
    clearFilters: (state) => {
      state.activeCompanyFilters = undefined;
      state.companiesSorting = undefined;
      state.companySearch = {
        query: '',
        field: [CompaniesFuzzySearchProps.Name],
        searchType: SearchType.Fuzzy,
      };
    },
  },
});

export const {
  setSelectedCompany,
  setWidgetFromCompanyListToDashboard,
  setWidgetFromCompanyDetailsToDashboard,
  setTotalNumberOfDashboardWidgets,
  addPendingFavourite,
  removePendingFavourites,
  revertPendingFavourites,
  setSortingOnActiveSearch,
  setFilterByIdOnActiveSearch,
  setSearchOrFilterQuery,
  setSearchField,
  setFuzzySearchField,
  setWildcardSearchField,
  setSearchOnActiveSearch,
  clearSearchOnActiveSearch,
  setFiltersByCountryCodeOnActiveSearch,
  appendCompaniesToList,
  setCompanyMapActiveCompnayId,
  setCompanies,
  clearFilters,
  setTotalCompaniesCount,
} = slice.actions;

export default slice.reducer;
